// The agent-facing script surface: the verbs from @lingjingai/screenplay
// plus scan/span, exposed as CLI commands. This replaces the previous 42
// query/edit/batch commands.
//
// The point of the shape: every parameter is a scalar, the address is one
// string, and the same address grammar works for read and write. The old
// surface asked an agent to learn per-verb address dialects (`ep_001/scn_003#5`
// vs `@ep:3/scn:2/ac:5` vs bare ids), plan digests for multi-point edits, and
// TSV parsing for every read.
import * as fs from "node:fs";

import { Command } from "commander";
import * as sp from "@lingjingai/screenplay";

import { CliError, EXIT_INPUT, EXIT_OK, type CommandOutput } from "../common.js";
import { scriptJsonPath } from "../common.js";
import { dispatch, describeArguments, type RunState, withScriptOpts } from "./registry.js";

type Opts = Record<string, unknown>;

const ADDR = "地址：集 `3`、场 `3-2`、单条 `3-2-5`、正式 ID、资产名、或 `<资产>/<状态>`";

function doc(opts: Opts): sp.Doc {
  const path = scriptJsonPath(opts);
  if (!fs.existsSync(path)) {
    throw new CliError("Local script file not found.", {
      exitCode: EXIT_INPUT,
      errorCode: "SCRIPT_NOT_FOUND",
      hint: "Run `scriptctl checkout`, `scriptctl create`, or pass --script-path to an existing script.",
      data: { expected: ["existing local script JSON"], actual: [path] },
    });
  }
  return sp.load(path);
}

function str(opts: Opts, key: string): string | undefined {
  const value = opts[key];
  // An empty string is a real value for --paren (set it to empty) and must not
  // be silently dropped; only a missing flag yields undefined.
  return typeof value === "string" ? value : undefined;
}

function num(opts: Opts, key: string): number | undefined {
  const value = opts[key];
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new CliError(`--${key} must be a number.`, {
      exitCode: EXIT_INPUT, errorCode: "OPTION_NOT_NUMBER",
      data: { actual: [String(value)] },
    });
  }
  return parsed;
}

function arg(opts: Opts, index = 0): string {
  const args = (opts["_args"] as string[] | undefined) ?? [];
  return args[index] ?? "";
}

function list(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === "string") return value.split(",").map((v) => v.trim()).filter(Boolean);
  return [];
}

/** SDK errors carry a plain message; map them onto scriptctl's error model. */
function text(run: () => string, op: string): [CommandOutput, number] {
  try {
    return [{ text: run(), data: { op, changed: false } }, EXIT_OK];
  } catch (error) {
    if (error instanceof CliError) throw error;
    throw new CliError(error instanceof Error ? error.message : String(error), {
      exitCode: EXIT_INPUT,
      errorCode: (error as { code?: string })?.code ?? "SCRIPT_TOOL_FAILED",
    });
  }
}

export function registerScriptCommands(program: Command, state: RunState): void {
  // ---- read -------------------------------------------------------------
  const read = withScriptOpts(program.command("read [addr]"), "read")
    .description(`读任意节点；不给地址返回剧本卡。${ADDR}`)
    .option("--outline", "只要场结构，不要正文")
    .option("--context <n>", "单条 action 前后各带几条（默认 3）")
    .option("--plain", "去掉锚点与时间戳（只读，不可回写）")
    .action(dispatch((opts) => text(() => sp.read(doc(opts), arg(opts), {
      ...(opts["outline"] ? { depth: "outline" as const } : {}),
      ...(opts["plain"] ? { plain: true } : {}),
      ...(num(opts, "context") !== undefined ? { context: num(opts, "context")! } : {}),
    }), "read"), state));
  describeArguments(read, [ADDR]);

  const scan = withScriptOpts(program.command("scan <episode>"), "read")
    .description("一次返回某集的完整上下文：场结构 + 全部正文 + 当前校验问题")
    .action(dispatch((opts) => text(() => {
      const d = doc(opts);
      const ep = arg(opts);
      return [
        `## 场结构\n${sp.read(d, ep, { depth: "outline" })}`,
        `## 正文\n${sp.read(d, ep)}`,
        `## 当前校验问题\n${sp.issues(d, {})}`,
      ].join("\n\n");
    }, "scan"), state));
  describeArguments(scan, ["集号，从 1 开始"]);

  const find = withScriptOpts(program.command("find [query]"), "read")
    .description("按正文正则 / 说话人 / 类型定位 action，返回锚点 + 命中窗口")
    .option("-i, --in <addr>", `限制范围，${ADDR}`)
    .option("-a, --speaker <name>", "人物名或 actor id")
    .option("-t, --type <type>", "action | dialogue | inner_thought | transition")
    .option("--limit <n>", "最多返回几条（默认 40）")
    .option("--full", "返回整条正文而不是命中窗口")
    .action(dispatch((opts) => text(() => sp.find(doc(opts), arg(opts), {
      ...(str(opts, "in") ? { in: str(opts, "in")! } : {}),
      ...(str(opts, "speaker") ? { speaker: str(opts, "speaker")! } : {}),
      ...(str(opts, "type") ? { type: str(opts, "type")! } : {}),
      ...(num(opts, "limit") !== undefined ? { limit: num(opts, "limit")! } : {}),
      ...(opts["full"] ? { full: true } : {}),
    }), "find"), state));
  describeArguments(find, ["正则；只按 speaker/type 过滤时可省略"]);

  const span = withScriptOpts(program.command("span <range>"), "read")
    .description("把一段 action 换算成集内视频时间窗口（半开区间）")
    .action(dispatch((opts) => text(() => sp.span(doc(opts), arg(opts)), "span"), state));
  describeArguments(span, [`${ADDR}，或 \`起ID..止ID\``]);

  withScriptOpts(program.command("validate"), "read")
    .description("跑整本校验")
    .action(dispatch((opts) => text(() => sp.validate(doc(opts)), "validate"), state));

  withScriptOpts(program.command("issues"), "read")
    .description("列当前校验问题")
    .option("--severity <severity>", "error | warning")
    .option("--code <code>", "按 issue code 过滤")
    .action(dispatch((opts) => text(() => sp.issues(doc(opts), {
      ...(str(opts, "severity") ? { severity: str(opts, "severity")! } : {}),
      ...(str(opts, "code") ? { code: str(opts, "code")! } : {}),
    }), "issues"), state));

  // ---- write ------------------------------------------------------------
  const edit = withScriptOpts(program.command("edit <addr>"), "write")
    .description(`改一个节点的字段；地址决定哪些字段合法。${ADDR}`)
    .option("--content <text>", "action 正文")
    .option("--paren <text>", "表演提示")
    .option("--no-paren", "删除表演提示字段")
    .option("--speaker <name>", "人物名或 `名字·状态`；旁白/广播/画外/群声亦可")
    .option("--ts <time>", "时间戳")
    .option("--no-ts", "删除时间戳")
    .option("--type <type>", "action | dialogue | inner_thought | transition")
    .option("--transition <spec>", "转场提示 `目标|过程|对比`")
    .option("--no-transition", "删除转场提示")
    .option("--name <text>", "资产 / 状态名")
    .option("--desc <text>", "资产 / 状态描述")
    .option("--importance <tier>", "featured | background")
    .option("--role <type>", "主角 | 配角")
    .option("--alias <list>", "资产别名，逗号分隔")
    .option("--space <space>", "场：interior | exterior")
    .option("--time <time>", "场：day | night | dawn | dusk")
    .option("--title <text>", "集 / 剧本标题")
    .option("--synopsis <text>", "集 / 剧本梗概")
    .option("--logline <text>", "剧本 logline")
    .option("--theme <text>", "剧本主题")
    .option("--worldview <text>", "剧本世界观")
    .option("--style <text>", "剧本风格")
    .option("--main <list>", "主要人物 `名字=定位,…`")
    .action(dispatch((opts) => {
      const fields: Record<string, unknown> = {};
      for (const key of ["content", "speaker", "type", "name", "desc", "importance",
        "role", "space", "time", "title", "synopsis", "logline", "theme",
        "worldview", "style", "main"]) {
        const value = str(opts, key);
        if (value !== undefined) fields[key] = value;
      }
      // commander turns --no-x into x === false; that is the "remove" signal.
      for (const key of ["paren", "ts", "transition"]) {
        if (opts[key] === false) fields[key] = null;
        else {
          const value = str(opts, key);
          if (value !== undefined) fields[key] = value;
        }
      }
      if (opts["alias"] !== undefined) fields["aliases"] = list(opts["alias"]);
      return text(() => sp.edit(doc(opts), arg(opts), fields as never), "edit");
    }, state));
  describeArguments(edit, [ADDR]);

  const insert = withScriptOpts(program.command("insert <addr> <content>"), "write")
    .description("在某条 action 前/后插入一条新 action")
    .requiredOption("-t, --type <type>", "action | dialogue | inner_thought | transition")
    .option("--before", "插到目标之前（默认之后）")
    .option("-a, --speaker <name>", "dialogue / inner_thought 必填")
    .option("--paren <text>", "表演提示")
    .action(dispatch((opts) => text(() => sp.insert(
      doc(opts), arg(opts), opts["before"] ? "before" : "after",
      str(opts, "type")!, arg(opts, 1), str(opts, "speaker"), str(opts, "paren"),
    ), "insert"), state));
  describeArguments(insert, ["相邻 action 的地址", "新 action 的正文"]);

  const move = withScriptOpts(program.command("move <addr> <to>"), "write")
    .description("把一条 action 挪到另一条旁边；跨场也保持 action_id")
    .option("--before", "挪到目标之前（默认之后）")
    .action(dispatch((opts) => text(() => sp.move(
      doc(opts), arg(opts), arg(opts, 1), opts["before"] ? "before" : "after",
    ), "move"), state));
  describeArguments(move, ["要挪的 action", "目标邻居 action"]);

  const write = withScriptOpts(program.command("write <addr>"), "write")
    .description("用 read 输出的同一份正文格式回写整场 / 整集 / 新集（`+`）")
    .option("-f, --file <path>", "正文文件；省略则读 stdin")
    .option("--allow-delete", "允许删除文本里消失的场 / action")
    .action(dispatch(async (opts) => {
      const fs = await import("node:fs");
      const file = str(opts, "file");
      const body = file ? fs.readFileSync(file, "utf8") : fs.readFileSync(0, "utf8");
      return text(() => sp.write(doc(opts), arg(opts), body, {
        allowDelete: Boolean(opts["allow_delete"]),
      }), "write");
    }, state));
  describeArguments(write, ["场 / 集地址，或 `+` 追加新集"]);

  const replace = withScriptOpts(program.command("replace <pattern> <to>"), "write")
    .description("批量替换正文；默认只预览，--apply 才落盘。过滤参数与 find 相同")
    .option("-i, --in <addr>", "限制范围")
    .option("-a, --speaker <name>", "限定说话人")
    .option("-t, --type <type>", "限定 action 类型")
    .option("--apply", "真的写入")
    .action(dispatch((opts) => text(() => sp.replace(doc(opts), arg(opts), arg(opts, 1), {
      ...(str(opts, "in") ? { in: str(opts, "in")! } : {}),
      ...(str(opts, "speaker") ? { speaker: str(opts, "speaker")! } : {}),
      ...(str(opts, "type") ? { type: str(opts, "type")! } : {}),
      ...(opts["apply"] ? { apply: true } : {}),
    }), "replace"), state));
  describeArguments(replace, ["正则", "替换成什么"]);

  const ref = withScriptOpts(program.command("ref <scene> <asset>"), "write")
    .description("改一个场对某资产的引用；--state 是完整状态集合而非追加")
    .option("--state <list>", "完整状态集合（名字或 ID），逗号分隔或重复给出", collect, [])
    .option("--remove", "删除整条引用")
    .action(dispatch((opts) => text(() => sp.ref(
      doc(opts), arg(opts), arg(opts, 1),
      list(opts["state"]).flatMap((v) => v.split(",")).map((v) => v.trim()).filter(Boolean),
      Boolean(opts["remove"]),
    ), "ref"), state));
  describeArguments(ref, ["场地址", "人物 / 地点 / 道具的名字或 ID"]);

  const asset = withScriptOpts(program.command("asset <kind> <name>"), "write")
    .description("登记一个新的 actor / location / prop")
    .requiredOption("--desc <text>", "描述")
    .option("--importance <tier>", "featured | background", "featured")
    .option("--alias <list>", "别名，逗号分隔")
    .action(dispatch((opts) => text(() => sp.asset(
      doc(opts), arg(opts) as never, arg(opts, 1), str(opts, "desc") ?? "",
      str(opts, "importance") ?? "featured", list(opts["alias"]),
    ), "asset"), state));
  describeArguments(asset, ["actor | location | prop", "名字"]);

  const stateCmd = withScriptOpts(program.command("state <asset> <name>"), "write")
    .description("给已有资产加一个状态（造型档位）")
    .requiredOption("--desc <text>", "描述")
    .action(dispatch((opts) => text(() => sp.state(
      doc(opts), arg(opts), arg(opts, 1), str(opts, "desc") ?? "",
    ), "state"), state));
  describeArguments(stateCmd, ["资产名或 ID", "状态名"]);

  const del = withScriptOpts(program.command("del <addr>"), "write")
    .description("删除 action / 场 / 集 / 资产 / 状态")
    .option("--replacement <state>", "删状态时把引用改写到这个状态")
    .option("--force", "容器非空时仍然删除")
    .action(dispatch((opts) => text(() => sp.del(doc(opts), arg(opts), {
      ...(str(opts, "replacement") ? { replacement: str(opts, "replacement")! } : {}),
      ...(opts["force"] ? { force: true } : {}),
    }), "del"), state));
  describeArguments(del, [ADDR]);

  const merge = withScriptOpts(program.command("merge <src> <dst>"), "write")
    .description("把一个资产并入另一个：全部场景引用与 speaker 改写过去")
    .action(dispatch((opts) => text(() => sp.merge(doc(opts), arg(opts), arg(opts, 1)),
      "merge"), state));
  describeArguments(merge, ["被并入的资产", "保留的资产"]);

  const ext = withScriptOpts(program.command("ext <addr> [key] [value]"), "write")
    .description("读或写任意节点的 extend 数据")
    .option("--remove", "删除该 key")
    .action(dispatch((opts) => text(() => sp.ext(
      doc(opts), arg(opts), arg(opts, 1) || undefined, arg(opts, 2) || undefined,
      Boolean(opts["remove"]),
    ), "ext"), state));
  describeArguments(ext, [ADDR, "extend key", "值"]);
}

function collect(value: string, previous: string[]): string[] {
  return [...previous, value];
}
